import {
	extractYouTubeVideoId,
	YouTubeEmbed,
	YouTubeFrame,
	YouTubePending,
	YouTubePlaceholder,
} from '@worldcoo/ui/components/youtube-embed';
import { useUsercentricsServiceConsent } from '@/lib/use-usercentrics-consent';
import { generateImageSrc } from '@/lib/shared';
import { onUsercentricsConsentChanged } from '@worldcoo/ui/lib/usercentrics';
import { useLingui } from '@lingui/react/macro';
import { lazy, Suspense, useEffect, useState } from 'react';

// Imported STATICALLY by the hero and cards, which is load-bearing. docs/notes/portals/video-embed.md
const SelfHostedVideo = lazy(() => import('./self-hosted-video'));

/** Must match the Usercentrics configuration exactly; a mismatch reads as `denied`, never leaks. */
const YOUTUBE_SERVICE_NAME = 'YouTube Video';

/** One prop so a caller cannot pass the src without the size that routes it through imgproxy. */
type Poster = { src: string; width: number; height: number };

export function VideoEmbed(props: { url: string; title?: string; className?: string; poster?: Poster }) {
	const { t } = useLingui();
	// Only YouTube is gated: the other branch streams from our own CDN.
	const youTubeConsent = useUsercentricsServiceConsent(YOUTUBE_SERVICE_NAME);
	const [activatedByDonor, setActivatedByDonor] = useState(false);
	const isYouTube = extractYouTubeVideoId(props.url) !== null;

	// 17 KB against the original's 389 KB, cached a year. docs/notes/portals/video-embed.md
	const posterSrc =
		props.poster && generateImageSrc(props.poster.src, { ...props.poster, quality: 60, format: 'webp' });

	// A CMP answer supersedes the one-off click, so refusing YouTube after "Play video" unmounts it. Cookies
	// already set on `youtube-nocookie.com` cannot be cleared from this origin.
	useEffect(() => onUsercentricsConsentChanged(() => setActivatedByDonor(false)), []);

	if (isYouTube) {
		if (youTubeConsent === 'denied' && !activatedByDonor) {
			return (
				<YouTubePlaceholder
					posterSrc={posterSrc}
					title={props.title}
					className={props.className}
					texts={{
						activate: t`Play video`,
						notice: t`Playing it loads content from YouTube, which may set cookies on your device.`,
					}}
					onActivate={() => setActivatedByDonor(true)}
				/>
			);
		}

		if (youTubeConsent === 'unknown' && !activatedByDonor) {
			return <YouTubePending posterSrc={posterSrc} title={props.title} className={props.className} />;
		}

		// Server-rendered from the consent cookie (`syncConsentCookie`). No poster: the hero image is the no-video
		// fallback. Consent from the banner is not a request to play.
		return <YouTubeEmbed url={props.url} title={props.title} className={props.className} autoPlay={activatedByDonor} />;
	}

	return (
		<Suspense fallback={<YouTubeFrame posterSrc={posterSrc} title={props.title} className={props.className} />}>
			<SelfHostedVideo url={props.url} title={props.title} className={props.className} />
		</Suspense>
	);
}
